/**
 * Attribution helper — captures UTM parameters + landing URL + referrer on
 * every page load, persists first-touch values in localStorage, and exposes
 * a single getAttribution() that the GHL webhook can merge into any form
 * submission.
 *
 * First-touch wins: once `utm_source` is captured from a paid/marketing URL,
 * subsequent visits don't overwrite it (so a user who clicks a Google Ad,
 * leaves, then returns directly and submits a form is still attributed to
 * Google Ads).
 *
 * Exceptions:
 *   - If first-touch is older than 90 days, it gets replaced (treat as a
 *     fresh visitor). 90 days matches Google Analytics' default lookback.
 *   - If first-touch is `direct` and the current visit carries ANY real
 *     signal — UTMs, a paid click-id, or a recognized/external referrer
 *     (e.g. a plain Google organic click with no UTMs) — upgrade
 *     first-touch. Direct is the weakest source and should lose to any
 *     observed channel.
 *   - If first-touch was only referrer-INFERRED (no explicit UTMs) and a
 *     later visit carries explicit UTMs / click-ids, the explicit signal
 *     wins (e.g. inferred google/organic upgraded by a tagged GMB click).
 *
 * Last-touch is stored separately for completeness — useful for "did Google
 * convert them or did Yelp close the deal" questions later.
 */

const STORAGE_KEY = 'dcc_first_touch';
const LAST_TOUCH_KEY = 'dcc_last_touch';
const TTL_DAYS = 90;

const UTM_KEYS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
];

function safeParse(value) {
	try {
		return value ? JSON.parse(value) : null;
	} catch (e) {
		return null;
	}
}

/**
 * Infer channel from a referrer hostname. Used when no explicit utm_source
 * is on the URL — normalizes search engines, social, and review sites to
 * canonical source names so resolvePhone() can match them.
 *
 * Returns null if the hostname doesn't match a known channel.
 */
function inferFromReferrer(hostname) {
	if (!hostname) return null;
	const h = hostname.toLowerCase();
	// Google search (www.google.com, google.co.uk, google.com.au, etc.)
	if (/(^|\.)google\./.test(h)) {
		return { utm_source: 'google', utm_medium: 'organic' };
	}
	// Bing
	if (/(^|\.)bing\./.test(h)) {
		return { utm_source: 'bing', utm_medium: 'organic' };
	}
	// DuckDuckGo
	if (/(^|\.)duckduckgo\./.test(h)) {
		return { utm_source: 'duckduckgo', utm_medium: 'organic' };
	}
	// Yahoo
	if (/(^|\.)yahoo\./.test(h)) {
		return { utm_source: 'yahoo', utm_medium: 'organic' };
	}
	// Facebook / Instagram (organic)
	if (/(^|\.)facebook\.|^m\.facebook\.|^l\.facebook\./.test(h) || h === 'fb.me') {
		return { utm_source: 'facebook', utm_medium: 'referral' };
	}
	if (/(^|\.)instagram\./.test(h)) {
		return { utm_source: 'instagram', utm_medium: 'referral' };
	}
	// Yelp
	if (/(^|\.)yelp\./.test(h)) {
		return { utm_source: 'yelp', utm_medium: 'referral' };
	}
	// Houzz / Angi / HomeAdvisor / BBB
	if (/(^|\.)houzz\./.test(h)) {
		return { utm_source: 'houzz', utm_medium: 'referral' };
	}
	if (/(^|\.)angi\.|(^|\.)angieslist\./.test(h)) {
		return { utm_source: 'angi', utm_medium: 'referral' };
	}
	if (/(^|\.)homeadvisor\./.test(h)) {
		return { utm_source: 'homeadvisor', utm_medium: 'referral' };
	}
	if (/(^|\.)bbb\./.test(h)) {
		return { utm_source: 'bbb', utm_medium: 'referral' };
	}
	return null;
}

/**
 * Read attribution data implied by the current page load.
 * Pulls UTMs from URL; falls back to gclid/referrer inference; finally direct.
 */
function readCurrentVisit() {
	if (typeof window === 'undefined') return null;
	const params = new URLSearchParams(window.location.search);
	const utms = {};
	let hasAnyUtm = false;
	for (const key of UTM_KEYS) {
		const value = params.get(key);
		if (value) {
			utms[key] = value;
			hasAnyUtm = true;
		}
	}

	// Click identifiers, in order of how reliably they indicate PAID intent:
	//
	//   gclid / gbraid / wbraid (Google) — set ONLY by Google Ads auto-tagging
	//     on paid clicks. Strong "this is a paid ad click" signal.
	//   msclkid (Microsoft) — set ONLY by Microsoft Advertising. Strong paid signal.
	//   fbclid (Facebook/Meta) — set on EVERY outbound link from Facebook /
	//     Instagram / Messenger, paid OR organic. So fbclid alone does NOT mean
	//     "paid ad click" — it just means "came from Meta somehow." Treating it
	//     as paid was the source of the Meta-misattribution bug.
	const hasGoogleAdsClick = params.has('gclid') || params.has('gbraid') || params.has('wbraid');
	const hasMicrosoftAdsClick = params.has('msclkid');
	const hasFacebookClick = params.has('fbclid');

	let referrer =
		typeof document !== 'undefined' ? document.referrer || '' : '';

	let referrerHost = '';
	if (referrer) {
		try {
			referrerHost = new URL(referrer).hostname;
		} catch (e) {
			/* ignore parse error */
		}
	}

	// Same-site referrer = internal navigation / hard reload of an inner
	// page. That's not a traffic source — without this guard a reload would
	// record "dccontrolsinc.com / referral" as a touch (and could even
	// overwrite a stored 'direct' first-touch under the upgrade rule below).
	if (referrerHost && referrerHost === window.location.hostname) {
		referrer = '';
		referrerHost = '';
	}

	// Resolve source + medium in priority order:
	//   1. Explicit utm_source / utm_medium on the URL (highest trust)
	//   2. Paid-click identifier in URL (gclid → google/cpc, etc.)
	//   3. Known referrer hostname (Google search, Yelp, etc.)
	//   4. Raw referrer hostname (still tracks the source even if unknown)
	//   5. 'direct' fallback
	let derivedSource = utms.utm_source || '';
	let derivedMedium = utms.utm_medium || '';

	if (!derivedSource && hasGoogleAdsClick) {
		derivedSource = 'google';
		if (!derivedMedium) derivedMedium = 'cpc';
	} else if (!derivedSource && hasMicrosoftAdsClick) {
		derivedSource = 'bing';
		if (!derivedMedium) derivedMedium = 'cpc';
	} else if (!derivedSource && hasFacebookClick) {
		// fbclid is set on ALL Facebook outbound links — organic posts,
		// page links, paid ads, Messenger. Treat as referral by default.
		// If the URL ALSO carries utm_medium=cpc/paid (manual tagging on a
		// paid ad), that explicit value wins via the utms branch above.
		derivedSource = 'facebook';
		if (!derivedMedium) derivedMedium = 'referral';
	}

	if (!derivedSource) {
		const inferred = inferFromReferrer(referrerHost);
		if (inferred) {
			derivedSource = inferred.utm_source;
			if (!derivedMedium) derivedMedium = inferred.utm_medium;
		}
	}

	if (!derivedSource && referrerHost) {
		// Unknown referrer — keep the raw hostname so we at least record it.
		derivedSource = referrerHost;
		if (!derivedMedium) derivedMedium = 'referral';
	}

	if (!derivedSource) derivedSource = 'direct';
	if (!derivedMedium) derivedMedium = referrer ? 'referral' : 'direct';

	const hadUtms =
		hasAnyUtm || hasGoogleAdsClick || hasMicrosoftAdsClick || hasFacebookClick;

	return {
		utm_source: derivedSource,
		utm_medium: derivedMedium,
		utm_campaign: utms.utm_campaign || '',
		utm_term: utms.utm_term || '',
		utm_content: utms.utm_content || '',
		// Raw Google Ads click identifiers — stored verbatim so they can be
		// sent back to Google Ads as offline/enhanced conversions later.
		// gclid is standard search; gbraid/wbraid are the iOS variants.
		gclid: params.get('gclid') || '',
		gbraid: params.get('gbraid') || '',
		wbraid: params.get('wbraid') || '',
		landing_url: window.location.href,
		referrer,
		captured_at: new Date().toISOString(),
		// Internal markers for the first-touch upgrade rules:
		//   _had_utms   — URL carried explicit UTMs or a paid click-id
		//                 (strongest signal; can replace an inferred source).
		//   _has_signal — we resolved ANY real source, explicit OR inferred
		//                 from an external referrer (e.g. google organic).
		//                 Beats 'direct' but never overwrites another source.
		_had_utms: hadUtms,
		_has_signal: hadUtms || (!!derivedSource && derivedSource !== 'direct'),
		// _paid — this visit carried a genuine PAID click-id (Google Ads gclid/
		// gbraid/wbraid or Microsoft msclkid). Used so a later paid click always
		// wins attribution over a non-paid first touch (organic/direct/referral),
		// which is what lets us actually measure paid performance. fbclid is
		// excluded on purpose — it's set on all Meta links, paid or not.
		_paid: hasGoogleAdsClick || hasMicrosoftAdsClick,
	};
}

/**
 * Called on every page load / route change. Captures the current visit,
 * applies first-touch rules, and persists both first- and last-touch.
 * Returns the persisted first-touch record (or null in SSR).
 */
export function captureAttribution() {
	if (typeof window === 'undefined') return null;
	const ls = window.localStorage;
	const current = readCurrentVisit();
	if (!current) return null;

	const existing = safeParse(ls.getItem(STORAGE_KEY));

	let firstTouch = existing;
	const now = Date.now();

	if (!existing) {
		firstTouch = current;
	} else {
		const existingTime = new Date(existing.captured_at || 0).getTime();
		const isStale = now - existingTime > TTL_DAYS * 86400 * 1000;

		// 'direct' first-touch loses to ANY real signal on a later visit —
		// including a referrer-inferred one (plain Google organic click).
		// Previously only explicit UTMs / click-ids could upgrade, so
		// "typed the URL once, Googled us later, submitted the form" stayed
		// misattributed as direct forever.
		const existingIsDirect =
			!existing.utm_source || existing.utm_source === 'direct';
		const currentHasSignal =
			current._has_signal !== undefined
				? current._has_signal
				: current._had_utms;

		const isUpgrade =
			(existingIsDirect && currentHasSignal) ||
			// Explicit UTMs / click-ids still trump a merely INFERRED source
			// (e.g. inferred google/organic → tagged GMB or paid click).
			(!existing._had_utms && current._had_utms) ||
			// A genuine PAID click always wins over a non-paid first touch, so
			// paid leads can't hide inside organic/direct. If they first came
			// via organic/GMB and later clicked a paid ad, credit the paid click.
			(current._paid && !existing._paid);

		if (isStale || isUpgrade) {
			firstTouch = current;
		}
	}

	if (firstTouch !== existing) {
		ls.setItem(STORAGE_KEY, JSON.stringify(firstTouch));
	}
	// Always update last-touch.
	ls.setItem(LAST_TOUCH_KEY, JSON.stringify(current));

	return firstTouch;
}

/**
 * Return a flat object suitable for merging directly into a form payload.
 * Keys are named to match what the GHL workflow expects.
 */
export function getAttribution() {
	if (typeof window === 'undefined') return {};
	const ft = safeParse(window.localStorage.getItem(STORAGE_KEY)) || {};
	const lt = safeParse(window.localStorage.getItem(LAST_TOUCH_KEY)) || {};

	return {
		// First-touch (primary attribution)
		utm_source: ft.utm_source || lt.utm_source || 'direct',
		utm_medium: ft.utm_medium || lt.utm_medium || '',
		utm_campaign: ft.utm_campaign || lt.utm_campaign || '',
		utm_term: ft.utm_term || lt.utm_term || '',
		utm_content: ft.utm_content || lt.utm_content || '',
		first_touch_landing_url: ft.landing_url || '',
		first_touch_referrer: ft.referrer || '',
		first_touch_at: ft.captured_at || '',

		// Google Ads click IDs (first-touch, last-touch fallback). Empty for
		// non-Google-Ads traffic. Used to tie a lead back to the exact click.
		gclid: ft.gclid || lt.gclid || '',
		gbraid: ft.gbraid || lt.gbraid || '',
		wbraid: ft.wbraid || lt.wbraid || '',

		// Last-touch (re-engagement source)
		last_touch_utm_source: lt.utm_source || '',
		last_touch_utm_medium: lt.utm_medium || '',
		last_touch_utm_campaign: lt.utm_campaign || '',
		last_touch_landing_url: lt.landing_url || '',
		last_touch_referrer: lt.referrer || '',
		last_touch_at: lt.captured_at || '',
	};
}

export default { captureAttribution, getAttribution };
